// Routed case-management page (F-cases). Loads the board once, then keeps it fresh off
// the case.updated SSE — mirroring RbacMatrix's load / live-refresh / optimistic-mutate
// shape. Quick-advance posts through the offline queue: a network drop parks the write
// and shows the queued banner; a real success is reconciled by the SSE refresh.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Case, CaseBoard as CaseBoardData } from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, PageHeader } from '../../ui/primitives.tsx';
import { CaseBoard } from './CaseBoard.tsx';
import { CaseList } from './CaseList.tsx';
import { NewCaseModal } from './NewCaseModal.tsx';

export function CasesPage() {
  const can = useCan();
  const navigate = useNavigate();
  const [board, setBoard] = useState<CaseBoardData | null>(null);
  const [view, setView] = useState<'board' | 'list'>('board');
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  const reloadBoard = useCallback(() => {
    api
      .get<CaseBoardData>('/cases/board')
      .then(setBoard)
      .catch(() => setError(t('cases.loadError')));
  }, []);

  useEffect(reloadBoard, [reloadBoard]);

  // A case moved (our own advance, another user's edit, or a replayed offline write) —
  // pull fresh board truth. This is the SSE half that keeps every open tab in sync.
  useLiveEvent('case.updated', () => {
    setQueued(false);
    reloadBoard();
  });

  async function advance(c: Case) {
    setError(null);
    try {
      // Optimistic + offline-safe: queue on a network drop; on a real success the
      // case.updated event refreshes the board, so we don't mutate locally here.
      const res = await api.post<Case>(`/cases/${c.id}/advance`, {}, { queue: true });
      if (isDeferred(res)) setQueued(true);
    } catch {
      setError(t('cases.advanceError'));
      reloadBoard();
    }
  }

  // The doctor-approval gate: sign off the digital setup (approval → production).
  async function approve(c: Case) {
    setError(null);
    try {
      const res = await api.post<Case>(`/cases/${c.id}/approve-setup`, {}, { queue: true });
      if (isDeferred(res)) setQueued(true);
    } catch {
      setError(t('cases.approveError'));
      reloadBoard();
    }
  }

  return (
    <div>
      <PageHeader title={t('cases.title')} subtitle={t('cases.subtitle')} />

      <div className="mb-4 flex items-center gap-2">
        <Button variant={view === 'board' ? 'primary' : 'ghost'} onClick={() => setView('board')}>
          {t('cases.view.board')}
        </Button>
        <Button variant={view === 'list' ? 'primary' : 'ghost'} onClick={() => setView('list')}>
          {t('cases.view.list')}
        </Button>
        {can('case:create') && (
          <Button className="ml-auto" onClick={() => setShowNew(true)}>
            {t('cases.new')}
          </Button>
        )}
      </div>

      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      {view === 'board' ? (
        board ? (
          <CaseBoard
            board={board}
            onAdvance={advance}
            onApprove={can('case:approve_setup') ? approve : undefined}
            onOpen={(c) => navigate(`/cases/${c.id}`)}
          />
        ) : (
          !error && <p className="text-muted">{t('common.loading')}</p>
        )
      ) : (
        <CaseList />
      )}

      <NewCaseModal open={showNew} onClose={() => setShowNew(false)} onCreated={reloadBoard} />
    </div>
  );
}
